// Полето „Причина" при изписване: свободен текст с подсказки от предишните
// причини (antd AutoComplete над `GET /inventory/writeoffs/reasons`).
//
//   фокус (и празно) → списъкът се отваря: най-ползваните първи, с броя им
//   писане           → филтър (многодумен, без значение на малки/главни букви)
//                      и оцветяване на съвпаденията
//   ↑/↓ + Enter      → избира; Enter без маркирана — затваря и пази написаното
//   Esc              → първо затваря списъка (preventDefault); второто е като
//                      на всяко поле — `useWorksheetKeyboard` води към търсачката
//   Tab              → затваря и минава нататък (не взима маркираната)
//   Ctrl/Cmd+Enter   → записът на листа, с изписването на съществуващата причина
//
// Написаното, което съвпада с вече ползвана причина (без значение на малки/
// главни букви и интервали), става НЕЙНОТО изписване при напускане на полето и
// при Ctrl+Enter — иначе филтърът на справката показва две причини вместо една.
//
// Стойностите на опциите са ключове, не текстът: rc-select в режим combobox сам
// маркира първата опция, която ЗАПОЧВА с написаното (и то с различаване на
// регистъра), и Enter я взима — „Изтекъл" ставаше „Изтекъл срок", а „изтекъл"
// оставаше. Тук се маркира само със стрелките или мишката.
import { useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { flushSync } from "react-dom";
import { AutoComplete } from "antd";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { useWriteoffReasons } from "../../../../hooks/useInventoryWriteoffReasons";
import { highlightWords } from "../../components/highlight";
import { KbdChip } from "../../components/KbdChip";
import {
  filterReasonSuggestions,
  findExistingReason,
  reasonSearchWords,
} from "./writeoffReasonSuggestions";

// Стойност на опция: префикс, който не се пише от клавиатура, + позицията
const OPTION_KEY_PREFIX = "\u0000wr:";
const optionKey = (index: number) => `${OPTION_KEY_PREFIX}${index}`;
const optionIndexOf = (value: string) =>
  value.startsWith(OPTION_KEY_PREFIX) ? Number(value.slice(OPTION_KEY_PREFIX.length)) : -1;

// Колоната в BE е VARCHAR(255)
const REASON_MAX_LENGTH = 255;

// Маркираната опция — кехлибареният тон на търсачката на листа (antd пише
// фона през cssinjs извън Tailwind layer-ите, затова `!`)
const POPUP_CLASS =
  "dk-elevated [&_.ant-select-item-option-active]:!bg-brand-tint";

type Props = {
  value: string;
  onChange: (reason: string) => void;
  placeholder: string;
};

export const WriteoffReasonField = ({ value, onChange, placeholder }: Props) => {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const { data: reasons = [] } = useWriteoffReasons();
  const [focused, setFocused] = useState(false);
  // Списъкът е затворен с Esc/избор/Enter — до следващото писане или фокус
  const [dismissed, setDismissed] = useState(false);
  // Натиснат Enter: rc-select в combobox ОТВАРЯ затворения списък с Enter —
  // тук Enter само избира/затваря, а Ctrl+Enter е записът на листа
  const enterDownRef = useRef(false);
  // Писано ли е от последния фокус — само тогава се търси съществуваща причина
  // (иначе само минаването през полето на стар документ щеше да го „променя")
  const editedRef = useRef(false);

  const words = useMemo(() => reasonSearchWords(value), [value]);
  const suggestions = useMemo(() => filterReasonSuggestions(reasons, value), [reasons, value]);
  const open = focused && !dismissed && suggestions.length > 0;

  const adoptExistingSpelling = () => {
    if (!editedRef.current) return;
    editedRef.current = false;
    const existing = findExistingReason(reasons, value);
    if (existing && existing !== value) onChange(existing);
  };

  const onKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") enterDownRef.current = true;
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      // Записът (`useWorksheetKeyboard`, window) върви веднага след нас от
      // същото натискане: затвореният списък и изписването на причината трябва
      // вече да са в DOM-а и в state-а на листа — затова синхронно
      flushSync(() => {
        setDismissed(true);
        adoptExistingSpelling();
      });
      return;
    }
    if (!open) return;
    if (event.key === "Escape") {
      // Първото Esc е на списъка — не стига до листа и до глобалната навигация
      event.preventDefault();
      setDismissed(true);
      return;
    }
    if (event.key === "Tab") {
      // rc-select взима маркираната с Tab и задържа фокуса — тук Tab само затваря
      // (синхронно: обработчикът на rc-select върви след нас от същото натискане)
      flushSync(() => setDismissed(true));
    }
  };

  const onOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setDismissed(true);
    else if (!enterDownRef.current) setDismissed(false);
  };

  const onTextChange = (next: string) => {
    // изборът на опция минава и оттук (с ключа) — поема го `onPick`
    if (optionIndexOf(next) >= 0) return;
    editedRef.current = true;
    setDismissed(false);
    onChange(next);
  };

  const onPick = (key: string) => {
    const picked = suggestions[optionIndexOf(key)];
    setDismissed(true);
    if (!picked) return;
    editedRef.current = false;
    onChange(picked.reason);
  };

  const options = suggestions.map((item, index) => ({
    value: optionKey(index),
    title: item.reason,
    label: (
      <div className="flex min-w-0 items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-[13.5px] leading-[normal] text-ink">
          {highlightWords(item.reason, words)}
        </span>
        <span
          title={t("inventoryPage.wrReasonUsed", { count: item.count })}
          className="shrink-0 font-[ui-monospace,Menlo,monospace] text-[11px] leading-[normal] text-muted"
        >
          {item.count}×
        </span>
      </div>
    ),
  }));

  return (
    <div
      onKeyDownCapture={onKeyDownCapture}
      onKeyDown={() => {
        enterDownRef.current = false;
      }}
    >
      <AutoComplete
        value={value}
        options={options}
        open={open}
        onOpenChange={onOpenChange}
        onChange={onTextChange}
        onSelect={onPick}
        onFocus={() => {
          editedRef.current = false;
          setFocused(true);
          setDismissed(false);
        }}
        onBlur={() => {
          setFocused(false);
          adoptExistingSpelling();
        }}
        filterOption={false}
        defaultActiveFirstOption={false}
        virtual={false}
        listHeight={300}
        maxLength={REASON_MAX_LENGTH}
        placeholder={placeholder}
        aria-label={t("inventoryPage.fieldReason")}
        popupMatchSelectWidth={isNarrow ? true : 340}
        classNames={{ popup: { root: POPUP_CLASS } }}
        popupRender={(menu) => (
          <>
            <div className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
              {t("inventoryPage.wrPreviousReasons")}
            </div>
            {menu}
            {isDesktop && (
              <div className="mt-1 flex items-center gap-3 border-t border-shade-2 px-3 pb-1 pt-2 text-[11px] leading-[normal] text-muted">
                <span className="flex items-center gap-1">
                  <KbdChip>↑↓</KbdChip> {t("inventoryPage.catKeyRow")}
                </span>
                <span className="flex items-center gap-1">
                  <KbdChip>Enter</KbdChip> {t("inventoryPage.wrKeyPick")}
                </span>
                <span className="flex items-center gap-1">
                  <KbdChip>Esc</KbdChip> {t("inventoryPage.catKeyEscClose")}
                </span>
              </div>
            )}
          </>
        )}
        className="w-full"
      />
    </div>
  );
};

export default WriteoffReasonField;
